import { useEffect, useState } from 'react';
import Modal from '@/components/base/Modal';
import { supabase } from '@/lib/supabase';
import { useLevels, activeLevelNames } from '@/hooks/useLevels';
import { DAY_OPTIONS, COLOR_OPTIONS } from '@/lib/levels';

interface GroupRow {
  id: string;
  name: string;
  level: string | null;
  day: string | null;
  day_note: string | null;
  time: string | null;
  whatsapp_link: string | null;
  color: string | null;
  active: boolean;
}

interface GrupModalProps {
  open: boolean;
  onClose: () => void;
  group: GroupRow | null;
}

export default function GrupModal({ open, onClose, group }: GrupModalProps) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { levels } = useLevels();
  const levelOptions = activeLevelNames(levels);

  useEffect(() => {
    if (!open) {
      setError(null);
      setSaving(false);
    }
  }, [open]);

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = e.currentTarget;
    const fd = new FormData(form);

    const name = (fd.get('name') as string || '').trim();
    const level = (fd.get('level') as string || '').trim() || null;
    const day = (fd.get('day') as string || '').trim() || null;
    const day_note = (fd.get('day_note') as string || '').trim() || null;
    const time = (fd.get('time') as string || '').trim() || null;
    const whatsapp_link = (fd.get('whatsapp_link') as string || '').trim() || null;
    const color = (fd.get('color') as string || '').trim() || '#6366f1';
    const active = fd.get('active') === 'on';

    if (!name) {
      setError('Lütfen grup adı girin.');
      return;
    }

    setError(null);
    setSaving(true);

    try {
      const payload = { name, level, day, day_note, time, whatsapp_link, color, active };

      if (group) {
        const { error: updateError } = await supabase
          .from('zehrahoca_groups')
          .update(payload)
          .eq('id', group.id);
        if (updateError) throw updateError;
      } else {
        const { error: insertError } = await supabase
          .from('zehrahoca_groups')
          .insert(payload);
        if (insertError) throw insertError;
      }

      form.reset();
      window.dispatchEvent(new Event('zehrahoca:groups-changed'));
      onClose();
    } catch (err) {
      setError((err as Error).message || 'Grup kaydedilirken bir hata oluştu.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={group ? 'Grup Düzenle' : 'Yeni Grup Ekle'}
      subtitle={group ? 'Grup bilgilerini güncelleyin.' : 'Yeni bir öğrenci grubu oluşturun.'}
    >
      <form onSubmit={handleSubmit} className="space-y-5">
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <label className="mb-1.5 block text-sm font-semibold text-foreground-800">Grup Adı</label>
            <input
              type="text"
              name="name"
              defaultValue={group?.name ?? ''}
              required
              placeholder="Örn: TYT Matematik A Grubu"
              className="w-full rounded-lg border border-background-300 bg-white px-3.5 py-2.5 text-sm text-foreground-950 outline-none transition focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
            />
          </div>

          <div>
            <label className="mb-1.5 block text-sm font-semibold text-foreground-800">Seviye</label>
            <select
              name="level"
              defaultValue={group?.level ?? ''}
              className="w-full rounded-lg border border-background-300 bg-white px-3.5 py-2.5 text-sm text-foreground-950 outline-none transition focus:border-primary-400 focus:ring-2 focus:ring-primary-100 cursor-pointer"
            >
              <option value="">Seçin</option>
              {levelOptions.map((l) => (
                <option key={l} value={l}>{l}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="mb-1.5 block text-sm font-semibold text-foreground-800">Ders Günü</label>
            <select
              name="day"
              defaultValue={group?.day ?? ''}
              className="w-full rounded-lg border border-background-300 bg-white px-3.5 py-2.5 text-sm text-foreground-950 outline-none transition focus:border-primary-400 focus:ring-2 focus:ring-primary-100 cursor-pointer"
            >
              <option value="">Seçin</option>
              {DAY_OPTIONS.map((d) => (
                <option key={d} value={d}>{d}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="mb-1.5 block text-sm font-semibold text-foreground-800">Ders Saati</label>
            <input
              type="text"
              name="time"
              defaultValue={group?.time ?? ''}
              placeholder="Örn: 19.00 - 20.00"
              className="w-full rounded-lg border border-background-300 bg-white px-3.5 py-2.5 text-sm text-foreground-950 outline-none transition focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
            />
          </div>

          <div className="sm:col-span-2">
            <label className="mb-1.5 block text-sm font-semibold text-foreground-800">Ders Günü Notu</label>
            <input
              type="text"
              name="day_note"
              defaultValue={group?.day_note ?? ''}
              placeholder="Örn: Salı ve Perşembe / Hafta sonu / Günler daha sonra netleşecek"
              className="w-full rounded-lg border border-background-300 bg-white px-3.5 py-2.5 text-sm text-foreground-950 outline-none transition focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
            />
          </div>

          <div>
            <label className="mb-1.5 block text-sm font-semibold text-foreground-800">Renk</label>
            <div className="flex flex-wrap items-center gap-2 pt-1">
              {COLOR_OPTIONS.map((c) => (
                <label key={c.value} className="cursor-pointer" title={c.label}>
                  <input
                    type="radio"
                    name="color"
                    value={c.value}
                    defaultChecked={(group?.color ?? '#6366f1') === c.value}
                    className="peer sr-only"
                  />
                  <span
                    className="flex h-8 w-8 items-center justify-center rounded-full border-2 border-transparent transition peer-checked:border-foreground-950"
                    style={{ backgroundColor: c.value }}
                  >
                    <span className="sr-only">{c.label}</span>
                  </span>
                </label>
              ))}
            </div>
          </div>

          <div className="sm:col-span-2">
            <label className="mb-1.5 block text-sm font-semibold text-foreground-800">WhatsApp Grup Linki</label>
            <input
              type="url"
              name="whatsapp_link"
              defaultValue={group?.whatsapp_link ?? ''}
              placeholder="https://chat.whatsapp.com/…"
              className="w-full rounded-lg border border-background-300 bg-white px-3.5 py-2.5 text-sm text-foreground-950 outline-none transition focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
            />
          </div>
        </div>

        <label className="flex cursor-pointer items-center gap-2 rounded-lg bg-background-50 p-4 text-sm text-foreground-800">
          <input
            type="checkbox"
            name="active"
            defaultChecked={group?.active ?? true}
            className="h-4 w-4 rounded border-background-300 text-primary-500 focus:ring-primary-400"
          />
          Grup aktif (dersler bu grupta devam ediyor)
        </label>

        {error && (
          <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3.5 py-3 text-sm text-red-700">
            <i className="ri-error-warning-line mt-0.5 text-base" />
            <span>{error}</span>
          </div>
        )}

        <div className="flex items-center justify-end gap-3 border-t border-background-200 pt-5">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg px-4 py-2.5 text-sm font-semibold text-foreground-700 transition hover:bg-background-100 cursor-pointer whitespace-nowrap"
          >
            İptal
          </button>
          <button
            type="submit"
            disabled={saving}
            className="rounded-lg bg-primary-500 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-primary-600 disabled:opacity-70 cursor-pointer whitespace-nowrap"
          >
            {saving ? 'Kaydediliyor...' : group ? 'Güncelle' : 'Grubu Kaydet'}
          </button>
        </div>
      </form>
    </Modal>
  );
}